import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, deleteDoc, doc, addDoc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Edit, Trash2, UserPlus, X, Plus, Loader2, Users, UserCheck, Wallet, TrendingUp } from "lucide-react";
import { ACTION_BTN_GOLD, ACTION_BTN_ICON } from "../styles/actionButtons";
import toast from "react-hot-toast";
import CaseStatCards from "../components/crm/cases/CaseStatCards";
import Pagination, { ITEMS_PER_PAGE } from "../components/Pagination";

import SelectMenu from "../components/crm/SelectMenu";
export const AddEditContactModal = ({ isOpen, onClose, contact, onSave }) => {
  const [formData, setFormData] = useState({
    salutation: "",
    name: "",
    company: "",
    group: "",
    email: "",
    contact: "",
    contactAlternate: "",
    gstin: "",
    primaryAddress: {
      building: "",
      street: "",
      city: "",
      district: "",
      state: "",
      pincode: "",
      country: "",
    },
    alternateAddresses: [],
    customFields: [],
  });

  const [showAlternateAddress, setShowAlternateAddress] = useState(false);
  const [alternateAddress, setAlternateAddress] = useState({
    building: "",
    street: "",
    city: "",
    district: "",
    state: "",
    pincode: "",
    country: "",
  });

  useEffect(() => {
    if (contact) {
      setFormData({
        salutation: contact.salutation || "",
        name: contact.name || "",
        company: contact.company || "",
        group: contact.group || "",
        email: contact.email || "",
        contact: contact.contact || "",
        contactAlternate: contact.contactAlternate || "",
        gstin: contact.gstin || "",
        primaryAddress: contact.primaryAddress || {
          building: "",
          street: "",
          city: "",
          district: "",
          state: "",
          pincode: "",
          country: "",
        },
        alternateAddresses: contact.alternateAddresses || [],
        customFields: contact.customFields || [],
      });
    } else {
      setFormData({
        salutation: "",
        name: "",
        company: "",
        group: "",
        email: "",
        contact: "",
        contactAlternate: "",
        gstin: "",
        primaryAddress: {
          building: "",
          street: "",
          city: "",
          district: "",
          state: "",
          pincode: "",
          country: "",
        },
        alternateAddresses: [],
        customFields: [],
      });
    }
    setShowAlternateAddress(false);
    setAlternateAddress({
      building: "",
      street: "",
      city: "",
      district: "",
      state: "",
      pincode: "",
      country: "",
    });
  }, [contact, isOpen]);

  const capitalizeWords = (str) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      setFormData((prev) => ({ ...prev, [name]: capitalizeWords(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddressChange = (e, type = "primary") => {
    const { name, value } = e.target;
    if (type === "primary") {
      setFormData((prev) => ({
        ...prev,
        primaryAddress: { ...prev.primaryAddress, [name]: value },
      }));
    } else {
      setAlternateAddress((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddAlternateAddress = () => {
    if (Object.values(alternateAddress).some((val) => val.trim() !== "")) {
      setFormData((prev) => ({
        ...prev,
        alternateAddresses: [...prev.alternateAddresses, alternateAddress],
      }));
      setAlternateAddress({
        building: "",
        street: "",
        city: "",
        district: "",
        state: "",
        pincode: "",
        country: "",
      });
      setShowAlternateAddress(false);
    }
  };

  const handleRemoveAlternateAddress = (index) => {
    setFormData((prev) => ({
      ...prev,
      alternateAddresses: prev.alternateAddresses.filter((_, i) => i !== index),
    }));
  };

  const handleAddCustomField = () => {
    setFormData((prev) => ({
      ...prev,
      customFields: [...prev.customFields, { key: "", value: "" }],
    }));
  };

  const handleCustomFieldChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedFields = [...prev.customFields];
      updatedFields[index][field] = value;
      return { ...prev, customFields: updatedFields };
    });
  };

  const handleRemoveCustomField = (index) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto pt-24">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-slate-900">
              {contact ? "Edit Contact" : "New Contact"}
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {/* Basic Details */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-600 mb-4">Basic Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-slate-500 mb-2">Salutation</label>
                <SelectMenu
                  name="salutation"
                  value={formData.salutation}
                  onChange={handleInputChange}
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                >
                  <option value="">Select</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                </SelectMenu>
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name of contact"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">This is a required field</p>
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Name of Company"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-slate-500 mb-2">Group</label>
                <input
                  type="text"
                  name="group"
                  value={formData.group}
                  onChange={handleInputChange}
                  placeholder="Enter group"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email of Contact"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm text-slate-500 mb-2">Contact</label>
                <input
                  type="tel"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="Enter phone number of Contact"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">Contact (Alternate)</label>
                <input
                  type="tel"
                  name="contactAlternate"
                  value={formData.contactAlternate}
                  onChange={handleInputChange}
                  placeholder="Enter alternate phone number of Contact"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm text-slate-500 mb-2">GSTIN</label>
              <input
                type="text"
                name="gstin"
                value={formData.gstin}
                onChange={handleInputChange}
                placeholder="Enter GST number"
                className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
              />
            </div>
          </div>

          {/* Primary Address */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-slate-600 mb-4">Contact's Address (Primary)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-500 mb-2">Building</label>
                <input
                  type="text"
                  name="building"
                  value={formData.primaryAddress.building}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Name/Number of Building"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">Street</label>
                <input
                  type="text"
                  name="street"
                  value={formData.primaryAddress.street}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Name of Street/Area"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.primaryAddress.city}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Name of City"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">District</label>
                <input
                  type="text"
                  name="district"
                  value={formData.primaryAddress.district}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Name of District"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.primaryAddress.state}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Name of State"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.primaryAddress.pincode}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Enter pin code of the area"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-500 mb-2">Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.primaryAddress.country}
                  onChange={(e) => handleAddressChange(e, "primary")}
                  placeholder="Name of Country"
                  className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
              </div>
            </div>

            {/* Display Alternate Addresses */}
            {formData.alternateAddresses.map((addr, index) => (
              <div key={index} className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-slate-600">Alternate Address {index + 1}</h4>
                  <button
                    type="button"
                    onClick={() => handleRemoveAlternateAddress(index)}
                    className="text-red-500 hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="text-sm text-slate-500">
                  {[addr.building, addr.street, addr.city, addr.district, addr.state, addr.pincode, addr.country]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              </div>
            ))}

            {/* Add Alternate Address Button/Form */}
            {!showAlternateAddress && (
              <button
                type="button"
                onClick={() => setShowAlternateAddress(true)}
                className="mt-4 text-sm text-slate-400 hover:text-slate-700 transition-colors flex items-center gap-1"
              >
                <Plus size={16} /> Add Alternate Address
              </button>
            )}

            {showAlternateAddress && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="text-sm font-medium text-slate-600 mb-3">New Alternate Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="building"
                    value={alternateAddress.building}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="Building"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    name="street"
                    value={alternateAddress.street}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="Street"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    name="city"
                    value={alternateAddress.city}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="City"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    name="district"
                    value={alternateAddress.district}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="District"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    name="state"
                    value={alternateAddress.state}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="State"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    name="pincode"
                    value={alternateAddress.pincode}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="Pincode"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                  <input
                    type="text"
                    name="country"
                    value={alternateAddress.country}
                    onChange={(e) => handleAddressChange(e, "alternate")}
                    placeholder="Country"
                    className="w-full bg-white text-slate-900 p-2 rounded border border-slate-300 focus:border-yellow-400 focus:outline-none text-sm"
                  />
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleAddAlternateAddress}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAlternateAddress(false);
                      setAlternateAddress({
                        building: "",
                        street: "",
                        city: "",
                        district: "",
                        state: "",
                        pincode: "",
                        country: "",
                      });
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Custom Fields */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-slate-600">Custom Fields</h3>
              <button
                type="button"
                onClick={handleAddCustomField}
                className="text-sm text-slate-400 hover:text-slate-700 transition-colors flex items-center gap-1"
              >
                <Plus size={16} /> Add Custom Field
              </button>
            </div>
            {formData.customFields.length > 0 && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm font-medium text-slate-500">
                  <div>Key</div>
                  <div>Value</div>
                </div>
                {formData.customFields.map((field, index) => (
                  <div key={index} className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={field.key}
                      onChange={(e) => handleCustomFieldChange(index, "key", e.target.value)}
                      placeholder="Enter Key"
                      className="w-full bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => handleCustomFieldChange(index, "value", e.target.value)}
                        placeholder="EnterValue"
                        className="flex-1 bg-white text-slate-900 p-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveCustomField(index)}
                        className="p-3 text-red-500 hover:text-red-400 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors font-medium text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 transition-colors font-semibold text-sm"
            >
              Save Client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddContactSearchModal = ({ isOpen, onClose, onAddPortalClient, onOpenManualForm, existingClients, isClientUser }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [adding, setAdding] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [clientsFetched, setClientsFetched] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setAdding(null);
      setSearched(false);
      setAllClients([]);
      setClientsFetched(false);
    }
  }, [isOpen]);

  // Fetch all clients once when modal opens
  useEffect(() => {
    if (isOpen && !isClientUser && !clientsFetched) {
      const fetchAllClients = async () => {
        try {
          const clientSnapshot = await getDocs(collection(db, "client"));
          const clients = clientSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            uid: docSnap.id,
            ...docSnap.data(),
          }));
          setAllClients(clients);
          setClientsFetched(true);
        } catch (error) {
          console.error("Error fetching clients:", error);
        }
      };
      fetchAllClients();
    }
  }, [isOpen, isClientUser, clientsFetched]);

  // Real-time filtering as user types
  useEffect(() => {
    if (!clientsFetched) return;
    const searchLower = searchQuery.toLowerCase().trim();

    if (searchLower.length < 2) {
      setSearchResults([]);
      setSearched(false);
      return;
    }

    const results = [];
    allClients.forEach((data) => {
      const name = (data.name || "").toLowerCase();
      const email = (data.email || "").toLowerCase();
      const phone = (data.phoneNumber || data.mobile || "").toLowerCase();

      if (name.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower)) {
        const alreadyAdded = existingClients.some(
          (c) => c.email && data.email && c.email.toLowerCase() === data.email.toLowerCase()
        );
        results.push({
          id: data.id,
          uid: data.uid,
          name: data.name || "",
          email: data.email || "",
          phone: data.phoneNumber || data.mobile || "",
          alreadyAdded,
          rawData: data,
        });
      }
    });

    setSearchResults(results);
    setSearched(true);
  }, [searchQuery, allClients, clientsFetched, existingClients]);

  const handleAddAsClient = async (portalClient) => {
    setAdding(portalClient.id);
    try {
      const clientData = {
        name: portalClient.name,
        email: portalClient.email,
        contact: portalClient.phone,
        portalClientUid: portalClient.uid,
        company: portalClient.rawData.company || "",
        salutation: portalClient.rawData.salutation || "",
        primaryAddress: portalClient.rawData.primaryAddress || {
          building: "", street: "", city: "", district: "", state: "", pincode: "", country: "",
        },
      };
      await onAddPortalClient(clientData);
      setSearchResults((prev) =>
        prev.map((r) => (r.id === portalClient.id ? { ...r, alreadyAdded: true } : r))
      );
    } catch (error) {
      console.error("Error adding client:", error);
    } finally {
      setAdding(null);
    }
  };

  const handleAddManually = () => {
    onClose();
    onOpenManualForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto pt-24">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-slate-900">Add Contact</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          {!isClientUser && (
            <p className="text-slate-500 text-sm mt-2">
              Search for a registered client by name or email. If not found, you can add manually.
            </p>
          )}
        </div>

        <div className="p-6">
          {/* Search Section - only for lawyers */}
          {!isClientUser && (
          <>
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={20} />
                <input
                  type="text"
                  placeholder="Start typing to search by name, email or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full bg-white text-slate-900 pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
                />
                {!clientsFetched && searchQuery.trim().length >= 2 && (
                  <Loader2 size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 animate-spin" />
                )}
              </div>
              {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
                <p className="text-gray-500 text-xs mt-1">Type at least 2 characters to search</p>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-3 max-h-64 overflow-y-auto mb-6">
                {searchResults.map((client) => (
                  <div
                    key={client.id}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-700 font-semibold">
                        {client.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <h4 className="text-slate-900 font-medium">{client.name}</h4>
                        {client.email && <p className="text-slate-500 text-sm">{client.email}</p>}
                        {client.phone && <p className="text-slate-500 text-sm">{client.phone}</p>}
                      </div>
                    </div>
                    <div>
                      {client.alreadyAdded ? (
                        <span className="text-green-400 text-sm font-medium px-4 py-2">Already Added</span>
                      ) : (
                        <button
                          onClick={() => handleAddAsClient(client)}
                          disabled={adding === client.id}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {adding === client.id ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <UserPlus size={16} />
                          )}
                          Add as Client
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Not found message with Add Manually button */}
            {searched && searchResults.length === 0 && (
              <div className="text-center py-6 mb-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-slate-500 mb-4">No registered clients found for "{searchQuery}"</p>
                <button
                  onClick={handleAddManually}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg text-sm font-medium flex items-center gap-2 mx-auto transition-colors"
                >
                  <UserPlus size={16} />
                  Add Contact Manually
                </button>
              </div>
            )}

            {/* Divider with "or" */}
            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 border-t border-slate-200"></div>
              <span className="text-gray-500 text-sm">or</span>
              <div className="flex-1 border-t border-slate-200"></div>
            </div>
          </>
          )}

          {/* Add Manually button - always visible */}
          <div className="text-center">
            <button
              onClick={handleAddManually}
              className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-medium flex items-center gap-2 mx-auto transition-colors"
            >
              <Plus size={16} />
              Add Contact Manually
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function MyClients() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showAddContactSearch, setShowAddContactSearch] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [invoiceStats, setInvoiceStats] = useState({});
  const [caseCounts, setCaseCounts] = useState({});

  useEffect(() => {
    if (globalUser?.uid) {
      fetchClients();
    }
  }, [globalUser]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredClients(clients);
    } else {
      // Case-insensitive search in name (without salutation), email, and contact
      const searchLower = searchQuery.toLowerCase().trim();
      const filtered = clients.filter(
        (client) =>
          client.name?.toLowerCase().includes(searchLower) ||
          client.email?.toLowerCase().includes(searchLower) ||
          client.contact?.toLowerCase().includes(searchLower)
      );
      setFilteredClients(filtered);
    }
  }, [searchQuery, clients]);

  // Aggregate invoice stats (pending due + total billed) per client contactId.
  useEffect(() => {
    const run = async () => {
      if (!globalUser?.uid || localStorage.getItem("userType") === "client") return;
      try {
        const snap = await getDocs(
          collection(db, "lawyers", globalUser.uid, "invoices")
        );
        const map = {};
        snap.forEach((d) => {
          const inv = d.data();
          const cid = inv.contactId;
          if (!cid) return;
          if (!map[cid]) map[cid] = { pendingDue: 0, totalBilled: 0, invoiceCount: 0 };
          map[cid].pendingDue += Number(inv.dueAmount || 0);
          map[cid].totalBilled += Number(inv.totalAmount || 0);
          map[cid].invoiceCount += 1;
        });
        setInvoiceStats(map);
      } catch (e) {
        console.error("Error fetching invoice stats:", e);
      }
    };
    run();
  }, [globalUser]);

  // Count pending cases shared with each registered client (by portalClientUid).
  useEffect(() => {
    const run = async () => {
      if (!globalUser?.uid) return;
      try {
        const snap = await getDocs(
          query(collection(db, "pending"), where("owner", "==", globalUser.uid))
        );
        const map = {};
        snap.forEach((d) => {
          const team = d.data().team;
          if (Array.isArray(team))
            team.forEach((uid) => {
              if (uid && uid !== globalUser.uid) map[uid] = (map[uid] || 0) + 1;
            });
        });
        setCaseCounts(map);
      } catch (e) {
        console.error("Error fetching case counts:", e);
      }
    };
    run();
  }, [globalUser]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, cityFilter]);

  // Open the edit modal automatically when arriving with ?edit=<clientId>
  // (used by the "Edit Client" button on the client detail page).
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && clients.length > 0) {
      const c = clients.find((x) => x.id === editId);
      if (c) {
        setEditingContact(c);
        setShowModal(true);
      }
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, clients, setSearchParams]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const clientsQuery = query(
        collection(db, "clients"),
        where("owner", "==", globalUser.uid)
      );
      const querySnapshot = await getDocs(clientsQuery);
      const clientsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setClients(clientsList);
      setFilteredClients(clientsList);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPortalClient = async (clientData) => {
    try {
      await addDoc(collection(db, "clients"), {
        ...clientData,
        owner: globalUser.uid,
        createdAt: serverTimestamp(),
        alternateAddresses: [],
        customFields: [],
      });
      toast.success(`${clientData.name} added as client successfully`);
      fetchClients();
    } catch (error) {
      console.error("Error adding portal client:", error);
      toast.error("Failed to add client");
    }
  };

  const handleAddContact = () => {
    setShowAddContactSearch(true);
  };

  const handleOpenManualForm = () => {
    setEditingContact(null);
    setShowModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleDeleteContact = async (id) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      try {
        await deleteDoc(doc(db, "clients", id));
        toast.success("Contact deleted successfully");
        fetchClients();
      } catch (error) {
        console.error("Error deleting contact:", error);
        toast.error("Failed to delete contact");
      }
    }
  };

  const handleSaveContact = async (formData) => {
    try {
      if (editingContact) {
        await updateDoc(doc(db, "clients", editingContact.id), {
          ...formData,
          updatedAt: serverTimestamp(),
        });
        toast.success("Contact updated successfully");
      } else {
        await addDoc(collection(db, "clients"), {
          ...formData,
          owner: globalUser.uid,
          createdAt: serverTimestamp(),
        });
        toast.success("Contact added successfully");
      }
      setShowModal(false);
      fetchClients();
    } catch (error) {
      console.error("Error saving contact:", error);
      toast.error("Failed to save contact");
    }
  };

  const formatINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  const chipSelectCls =
    "rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-600 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  // Enrich each client with derived data from invoices/cases (real fields; the
  // "clients" collection itself has no status / due / case-count columns).
  const enrich = (c) => {
    const inv = invoiceStats[c.id] || { pendingDue: 0, totalBilled: 0, invoiceCount: 0 };
    const caseCount = c.portalClientUid ? caseCounts[c.portalClientUid] || 0 : 0;
    const isActive =
      Boolean(c.portalClientUid) ||
      inv.pendingDue > 0 ||
      inv.totalBilled > 0 ||
      caseCount > 0;
    return {
      ...c,
      _city: c.primaryAddress?.city || "",
      _pendingDue: inv.pendingDue,
      _totalBilled: inv.totalBilled,
      _caseCount: caseCount,
      _isActive: isActive,
    };
  };

  const enrichedAll = clients.map(enrich);
  const stats = {
    total: enrichedAll.length,
    active: enrichedAll.filter((c) => c._isActive).length,
    pendingDue: enrichedAll.reduce((s, c) => s + c._pendingDue, 0),
    billed: enrichedAll.reduce((s, c) => s + c._totalBilled, 0),
  };
  const cityOptions = [
    ...new Set(enrichedAll.map((c) => c._city).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));

  const visibleClients = filteredClients.map(enrich).filter(
    (c) =>
      (statusFilter === "all" ||
        (statusFilter === "active" ? c._isActive : !c._isActive)) &&
      (cityFilter === "all" || c._city === cityFilter)
  );
  const pageClients = visibleClients.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="min-h-screen relative bg-[#F8FAFC] text-gray-900">
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Clients
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Manage your clients and their details
            </p>
          </div>
          <button onClick={handleAddContact} className={ACTION_BTN_GOLD}>
            <UserPlus className={ACTION_BTN_ICON} />
            Add Client
          </button>
        </div>

        {/* Stat cards */}
        <CaseStatCards
          className="mt-4"
          cards={[
            { label: "Total Clients", value: stats.total, hint: "View all", Icon: Users, tint: "blue", onClick: () => { setStatusFilter("all"); setCityFilter("all"); setSearchQuery(""); } },
            { label: "Active Clients", value: stats.active, hint: "View active", Icon: UserCheck, tint: "green", onClick: () => setStatusFilter("active") },
            { label: "Pending Dues", value: formatINR(stats.pendingDue), hint: "From invoices", Icon: Wallet, tint: "amber", onClick: () => {} },
            { label: "Total Billed", value: formatINR(stats.billed), hint: "All time", Icon: TrendingUp, tint: "purple", onClick: () => {} },
          ]}
        />

        {/* Filter row */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40"
            />
          </div>
          <SelectMenu value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={chipSelectCls} aria-label="Filter by status">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </SelectMenu>
          <SelectMenu value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className={chipSelectCls} aria-label="Filter by city">
            <option value="all">City</option>
            {cityOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
          </SelectMenu>
          {(searchQuery || statusFilter !== "all" || cityFilter !== "all") && (
            <button
              type="button"
              onClick={() => { setSearchQuery(""); setStatusFilter("all"); setCityFilter("all"); }}
              className="inline-flex items-center rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800"
            >
              Clear
            </button>
          )}
        </div>

        {/* Clients table */}
        <div className="mt-4">
          {loading ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex items-center gap-4 border-b border-slate-100 px-4 py-4 last:border-0">
                  <div className="h-9 w-9 animate-pulse rounded-full bg-slate-100" />
                  <div className="h-4 flex-1 animate-pulse rounded bg-slate-100" />
                  <div className="hidden h-4 w-24 animate-pulse rounded bg-slate-100 sm:block" />
                  <div className="h-5 w-16 animate-pulse rounded-full bg-slate-100" />
                </div>
              ))}
            </div>
          ) : visibleClients.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <UserPlus size={40} className="mx-auto mb-3 opacity-40" />
              <p className="text-lg">
                {clients.length === 0 ? "No clients yet." : "No clients match your filters."}
              </p>
              {clients.length === 0 && (
                <p className="text-sm">Click “Add Client” to get started.</p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[860px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-3">Client / Contact</th>
                        <th className="px-4 py-3">Email / Phone</th>
                        <th className="px-4 py-3">City</th>
                        <th className="px-4 py-3">Cases</th>
                        <th className="px-4 py-3">Pending Due</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pageClients.map((c) => {
                        const fullName = `${c.salutation ? c.salutation + " " : ""}${c.name || ""}`.trim();
                        const initial = (c.name || "?").trim().charAt(0).toUpperCase();
                        return (
                          <tr
                            key={c.id}
                            onClick={() => navigate(`/portal/client/${c.id}`)}
                            className="cursor-pointer transition-colors hover:bg-slate-50"
                          >
                            <td className="px-4 py-3 align-top">
                              <div className="flex items-center gap-3">
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-yellow-100 text-xs font-bold text-yellow-700">
                                  {initial}
                                </span>
                                <div className="min-w-0">
                                  <div className="truncate font-semibold text-slate-900" title={fullName}>
                                    {fullName || "Unnamed"}
                                  </div>
                                  {c.company && (
                                    <div className="truncate text-xs text-slate-500">{c.company}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <div className="max-w-[220px] truncate text-slate-700" title={c.email}>
                                {c.email || "—"}
                              </div>
                              {c.contact && <div className="text-xs text-slate-500">{c.contact}</div>}
                            </td>
                            <td className="px-4 py-3 align-top text-slate-700">{c._city || "—"}</td>
                            <td className="px-4 py-3 align-top text-slate-700">{c._caseCount || 0}</td>
                            <td className="px-4 py-3 align-top font-medium text-slate-800">
                              {c._pendingDue > 0 ? formatINR(c._pendingDue) : "—"}
                            </td>
                            <td className="px-4 py-3 align-top">
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                                  c._isActive
                                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                    : "bg-slate-100 text-slate-600 ring-slate-200"
                                }`}
                              >
                                {c._isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-4 py-3 align-top text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleEditContact(c)}
                                  title="Edit"
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteContact(c.id)}
                                  title="Delete"
                                  className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <Pagination
                currentPage={currentPage}
                totalItems={visibleClients.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
              />
            </>
          )}
        </div>
      </main>

      <AddEditContactModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contact={editingContact}
        onSave={handleSaveContact}
      />

      <AddContactSearchModal
        isOpen={showAddContactSearch}
        onClose={() => setShowAddContactSearch(false)}
        onAddPortalClient={handleAddPortalClient}
        onOpenManualForm={handleOpenManualForm}
        existingClients={clients}
        isClientUser={localStorage.getItem("userType") === "client"}
      />
    </div>
  );
}

export default MyClients;
